import { Vehicle } from '@esportal/portal-service';
import { checkOldCar, normalizeToTimestamp } from './common-helpers';
import { COLOR_TOKEN, progressColor } from './color-tokens';

export function getMonthsBetweenDates(startDate: number | string, endDate: number | string): number {
	const start = new Date(normalizeToTimestamp(startDate));
	const end = new Date(normalizeToTimestamp(endDate));

	let months = (end.getFullYear() - start.getFullYear()) * 12;
	months += end.getMonth() - start.getMonth();

	if (end.getDate() < start.getDate()) {
		months--;
	}

	return Math.max(0, months);
}

export function hasContract(dateInput?: number | string): boolean {
	if (!dateInput) return false;
	const timestamp = normalizeToTimestamp(dateInput);
	return timestamp - Date.now() >= 0;
}

export function getServiceStatusColor(dateInput: number | string, diffDaysCheck?: number): string {
	const today = Date.now();
	const timestamp = normalizeToTimestamp(dateInput);
	const diffInDays = (timestamp - today) / (1000 * 60 * 60 * 24);

	let colorToUse = `${COLOR_TOKEN.signal.success}`;
	if (diffInDays < 0) {
		colorToUse = `${COLOR_TOKEN.signal.error}`;
	} else if (diffDaysCheck && diffInDays <= diffDaysCheck) {
		colorToUse = `${progressColor}`;
	}
	return colorToUse;
}

export function formatUnixToSpanishDate(dateInput: number | string): string {
	const months = [
		'enero',
		'febrero',
		'marzo',
		'abril',
		'mayo',
		'junio',
		'julio',
		'agosto',
		'septiembre',
		'octubre',
		'noviembre',
		'diciembre',
	];

	const timestamp = normalizeToTimestamp(dateInput);
	const date = new Date(timestamp);
	const day = date.getDate();
	const month = months[date.getMonth()];
	const year = date.getFullYear();

	return `${day} de ${month} de ${year}`;
}

export function IDCareExpireDate(dateTimeStamp: number) {
	const licenseDate = new Date(dateTimeStamp);
	const expireDate = new Date(licenseDate);
	expireDate.setFullYear(expireDate.getFullYear() + 8);

	return expireDate.getTime();
}

export function isLongdriveActive(vehicle: Vehicle): boolean {
	return checkOldCar(vehicle.licenseDate, 14);
}

export function longdriveProduct(vehicle: Vehicle): string {
	if (!vehicle?.longdrive) return '';

	if (vehicle?.longdrive.isPro) {
		return 'Pro';
	} else {
		if (vehicle?.longdrive.contractType == 'MT LD') return 'Advance';
		else if (vehicle?.longdrive.contractType == 'MT LM') return 'Standard';
	}
	return '';
}

function hasValidWarrantyInfo(vehicle: Vehicle): boolean {
	const requiredFields =
		vehicle.warranty.flagVNGarantia !== undefined &&
		vehicle.warranty.flagVNGarantiaExtendida !== undefined &&
		vehicle.warranty.warrantyEndingDate !== undefined &&
		vehicle.warranty.anyosCoberturaGarantia !== undefined &&
		vehicle.warranty.flagHiredWarrantyExtension !== undefined;

	if (!requiredFields) return false;

	if (vehicle.warranty.flagHiredWarrantyExtension === true) {
		return (
			vehicle.warranty.warrantyExtensionYears !== undefined &&
			vehicle.warranty.warrantyCoverageKms !== undefined &&
			vehicle.warranty.codPRExtensionGarantia !== undefined
		);
	}

	return true;
}

export function isWarrantyActive(vehicle: Vehicle): vehicle is Vehicle & {
	warranty: Required<Pick<Vehicle['warranty'], 'warrantyEndingDate'>>;
} {
	if (
		hasValidWarrantyInfo(vehicle) &&
		vehicle.flagIsVO === false &&
		(vehicle.warranty.flagVNGarantia || vehicle.warranty.flagVNGarantiaExtendida) &&
		vehicle.licenseDate
	) {
		return true;
	}
	return false;
}

export function manufacturerWarrantyTotalMonths(vehicle: Vehicle): number {
	if (!vehicle.warranty.anyosCoberturaGarantia) return 0;
	return vehicle.warranty.anyosCoberturaGarantia * 12;
}

export function manufacturerWarrantyRemainingMonths(vehicle: Vehicle): number {
	if (!vehicle.licenseDate || !vehicle.warranty.anyosCoberturaGarantia) return 0;

	const licenseTimestamp = new Date(vehicle.licenseDate).getTime();
	const totalMonths = vehicle.warranty.anyosCoberturaGarantia * 12;

	const usedMonths = getMonthsBetweenDates(licenseTimestamp, Date.now());

	return Math.max(0, totalMonths - usedMonths);
}

export function hiredWarrantyRemainingMonths(vehicle: Vehicle): number {
	if (!vehicle.warranty.warrantyEndingDate) return 0;

	const now = Date.now();
	const endDate = new Date(vehicle.warranty.warrantyEndingDate).getTime();

	return Math.max(0, getMonthsBetweenDates(now, endDate));
}

export function hiredWarrantyTotalMonths(vehicle: Vehicle): number {
	if (!vehicle.warranty.warrantyEndingDate || !vehicle.licenseDate) return 0;

	const startDate = new Date(vehicle.licenseDate).getTime();
	const endDate = new Date(vehicle.warranty.warrantyEndingDate).getTime();

	return getMonthsBetweenDates(startDate, endDate);
}

export function isIDcareActive(vehicle: Vehicle): boolean {
	return (
		vehicle.comercialDenomination.includes('ID.') && checkOldCar(vehicle.licenseDate, 8) && vehicle.climaproKm < 160000
	);
}

export function manufacturerWarrantyEndDate(
	licenseDate: number | string,
	anyosCoberturaGarantia: number,
): number | null {
	if (!licenseDate || !anyosCoberturaGarantia) return null;

	const normalizedLicenseDate = new Date(normalizeToTimestamp(licenseDate));
	const endDate = new Date(normalizedLicenseDate);
	endDate.setFullYear(endDate.getFullYear() + anyosCoberturaGarantia);

	return endDate.getTime();
}
